import { cn } from "@u/cn";
import Button from "@ui/button";
import { LayoutGrid, List, Table2, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DashboardView } from "./queryState";

const ICON: Record<DashboardView, LucideIcon> = {
  table: Table2,
  list: List,
  grid: LayoutGrid,
};

const LABEL_KEY: Record<DashboardView, string> = {
  table: "ds_view_table",
  list: "ds_view_list",
  grid: "ds_view_grid",
};

export interface DashboardViewSwitcherProps {
  value: DashboardView;
  options?: DashboardView[];
  onChange(view: DashboardView): void;
  className?: string;
}

export function DashboardViewSwitcher({
  value,
  options = ["table", "list", "grid"],
  onChange,
  className,
}: DashboardViewSwitcherProps) {
  const { t } = useTranslation();
  return (
    <div
      role="group"
      className={cn(
        "inline-flex items-center gap-1 rounded-xl border border-border/40 p-1",
        className,
      )}
    >
      {options.map((view) => {
        const Icon = ICON[view];
        const active = view === value;
        const label = t(LABEL_KEY[view], { defaultValue: view });
        return (
          <Button
            key={view}
            design={active ? "default" : "ghost"}
            size="icon"
            icon={Icon}
            aria-label={label}
            floatingLabel={label}
            floatingPosition="bottom"
            aria-pressed={active}
            onClick={() => onChange(view)}
            className="rounded-lg h-11 w-11 sm:h-8 sm:w-8"
          />
        );
      })}
    </div>
  );
}
